import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import posthog from 'posthog-js';
import { BehaviorSubject, map, mergeMap, tap } from 'rxjs';
import { LambdaService } from '../../shared/core/lambda.service';
import { BoardHolderService } from '../board/board.holder.service';
import { CoreItemType } from '../core-item/core-item';
import { CoreItemService } from '../core-item/core-item.service';
import { OpenaiService } from '../openai/openai.service';
import { PostHogEvent } from '../posthog/posthog.event';
import { WorkspaceHolderService } from '../workspace/workspace.holder.service';
import { Objection, ObjectionType, ObjectionUnsafeSubData } from './objection';

@Injectable({
  providedIn: 'root',
})
export class ObjectionService {
  private _currentChangeCount = 0;

  private _changeSubject = new BehaviorSubject<number>(0);

  changed$ = this._changeSubject.asObservable();

  constructor(
    private workspaceHolderService: WorkspaceHolderService,
    private coreItemService: CoreItemService,
    private lambdaService: LambdaService,
    private openaiService: OpenaiService,
    private boardHolderService: BoardHolderService
  ) {}

  change() {
    this._changeSubject.next(this._currentChangeCount++);
  }

  getItemById$(id: string) {
    return this.coreItemService.getItemByAction$({ id }).pipe(map((c) => c as Objection));
  }

  itemList$() {
    const boardId = this.boardHolderService.myPersonalBoard()!.id;
    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        return this.coreItemService
          .getCoreItemListByBoard$(boardId, {
            coreTypeList: [CoreItemType.objection],
          })
          .pipe(
            map((list) => {
              list.sort((a, b) => ((a.createdTime ?? DateTime.local()) < (b.createdTime ?? DateTime.local()) ? 1 : -1));
              return list as Objection[];
            })
          );
      })
    );
  }

  update$(objection: Objection) {
    posthog.capture(PostHogEvent.objectionUpdate);
    return this.coreItemService.updateCoreItem$(objection).pipe(
      tap((_) => {
        this.change();
      })
    );
  }

  addItem$(title: string, description: string, type: ObjectionType, unsafeSubData: ObjectionUnsafeSubData) {
    posthog.capture(PostHogEvent.objectionAdd);

    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        addMetadataList: [
          {
            title,
            description,
            type,
            coreType: CoreItemType.objection,
            unsafeSubData,
          },
        ],
      })
      .pipe(
        map((arr) => {
          this.change();
          return arr;
        })
      );
  }

  deleteItemList$(itemList: Objection[]) {
    const deleteMetadataList = itemList.map((c) => ({
      id: c.id,
      sk: c.sk,
    }));

    posthog.capture(PostHogEvent.objectionDelete);

    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        deleteMetadataList,
      })
      .pipe(
        tap((_) => {
          this.change();
        })
      );
  }
}
